import { z } from 'zod';

/** Типы источника заметки на фронте. */
export const noteSourceTypeSchema = z.enum(['document', 'analysis', 'manual', 'news']);
export const noteAnchorTypeSchema = z.enum(['text', 'point', 'area', 'none']);

const toRecordOrNull = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const toNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const toStringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const normalizeSourceType = (value: unknown): NoteSourceType | null => {
  const parsed = noteSourceTypeSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

const normalizeAnchorRects = (value: unknown): Array<{ x: number; y: number; width: number; height: number }> => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<Array<{ x: number; y: number; width: number; height: number }>>((acc, item) => {
    const raw = toRecordOrNull(item);

    if (!raw) {
      return acc;
    }

    const x = toNumberOrNull(raw.x);
    const y = toNumberOrNull(raw.y);
    const width = toNumberOrNull(raw.width ?? raw.w);
    const height = toNumberOrNull(raw.height ?? raw.h);

    if (x === null || y === null || width === null || height === null) {
      return acc;
    }

    acc.push({ x, y, width, height });

    return acc;
  }, []);
};

const normalizeAnchorPoint = (value: unknown): { x: number; y: number } | null => {
  const raw = toRecordOrNull(value);

  if (!raw) {
    return null;
  }

  const x = toNumberOrNull(raw.x);
  const y = toNumberOrNull(raw.y);

  if (x === null || y === null) {
    return null;
  }

  return { x, y };
};

type NoteAnchorType = z.infer<typeof noteAnchorTypeSchema>;

/** API отдаёт один объект anchor (type, document_page, quote, rects, point, path, color). */
const parseAnchorFromPayload = (
  anchor: unknown,
): {
  anchorType: NoteAnchorType | null;
  anchorPageNumber: number | null;
  anchorQuote: string;
  anchorRects: Array<{ x: number; y: number; width: number; height: number }>;
  anchorPoint: { x: number; y: number } | null;
  anchorPath: string | null;
  anchorColor: string | null;
} => {
  const anchorData = toRecordOrNull(anchor);

  if (!anchorData) {
    return {
      anchorType: null,
      anchorPageNumber: null,
      anchorQuote: '',
      anchorRects: [],
      anchorPoint: null,
      anchorPath: null,
      anchorColor: null,
    };
  }
  const typeVal = toStringOrNull(anchorData.type);
  const anchorType: NoteAnchorType | null =
    typeVal === 'text' || typeVal === 'point' || typeVal === 'area' || typeVal === 'none' ? typeVal : null;
  const page = toNumberOrNull(anchorData.document_page);

  return {
    anchorType: anchorType === 'none' ? null : anchorType,
    anchorPageNumber: page,
    anchorQuote: toStringOrNull(anchorData.quote) ?? '',
    anchorRects: normalizeAnchorRects(anchorData.rects),
    anchorPoint: normalizeAnchorPoint(anchorData.point),
    anchorPath: toStringOrNull(anchorData.path),
    anchorColor: toStringOrNull(anchorData.color),
  };
};

export const noteSchema = z
  .object({
    id: z.number().int(),
    caption: z.string().nullish(),
    text: z.string(),
    is_deleted: z.boolean(),
    artifact_type: z.string().nullish(),
    artifact_id: z.string().nullish(),
    artifact_name: z.string().nullish(),
    anchor: z.unknown().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
  })
  .transform((raw) => {
    const anchorFields = parseAnchorFromPayload(raw.anchor);

    return {
      id: raw.id,
      caption: raw.caption ?? '',
      text: raw.text,
      isDeleted: raw.is_deleted,
      artifactType: normalizeSourceType(raw.artifact_type),
      artifactId: raw.artifact_id ?? null,
      artifactName: raw.artifact_name ?? null,
      anchor: raw.anchor,
      ...anchorFields,
      createdAt: raw.created_at,
      updatedAt: raw.updated_at,
    };
  });

export type NoteEntityApi = z.input<typeof noteSchema>;
export type NoteEntity = z.infer<typeof noteSchema>;
export type NoteSourceType = z.infer<typeof noteSourceTypeSchema>;
export type { NoteAnchorType };
